import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, ChevronLeft, ChevronRight, Play, Image as ImageIcon, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminWorkLogs = () => {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [filterWorker, setFilterWorker] = useState('all');
  const [logs, setLogs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingMedia, setViewingMedia] = useState(null);

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/workers`, { withCredentials: true });
      setWorkers(res.data.filter(w => w.role !== 'admin'));
    } catch (e) { /* silenced */ }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (filterWorker !== 'all') params.user_id = filterWorker;
      const res = await axios.get(`${API_URL}/api/admin/work-logs`, {
        params, withCredentials: true
      });
      setLogs(res.data);
    } catch (e) { /* silenced */ }
    finally { setLoading(false); }
  }, [selectedDate, filterWorker]);

  useEffect(() => { fetchWorkers(); }, [fetchWorkers]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const shiftDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const formatDate = (d) => {
    const dt = new Date(d + 'T12:00:00');
    const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    return `${days[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
  };

  const viewMedia = async (item) => {
    try {
      const res = await axios.get(`${API_URL}/api/files/${item.path}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      setViewingMedia({ ...item, url });
    } catch (e) { alert('Errore caricamento file'); }
  };

  return (
    <>
      <Card className="border-[#E4E4E7] shadow-sm" data-testid="admin-work-logs-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#09090B] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#8B5CF6]" />
            Diario Lavorazioni
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Controls */}
          <div className="flex gap-2 items-center">
            <div className="flex items-center bg-[#F4F4F5] rounded-lg flex-1">
              <button onClick={() => shiftDate(-1)} className="p-1.5 hover:bg-white rounded-l-lg" data-testid="admin-log-prev">
                <ChevronLeft className="w-4 h-4 text-[#71717A]" />
              </button>
              <div className="flex-1 text-center py-1">
                <p className="text-xs font-semibold text-[#09090B]">{formatDate(selectedDate)}</p>
              </div>
              <button onClick={() => shiftDate(1)} className="p-1.5 hover:bg-white rounded-r-lg" data-testid="admin-log-next">
                <ChevronRight className="w-4 h-4 text-[#71717A]" />
              </button>
            </div>
            <Select value={filterWorker} onValueChange={setFilterWorker}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="admin-log-worker-filter">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli operai</SelectItem>
                {workers.map(w => (
                  <SelectItem key={w.user_id} value={w.user_id}>
                    {w.name} {w.surname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Logs */}
          {loading ? (
            <p className="text-center text-sm text-[#71717A] py-4">Caricamento...</p>
          ) : logs.length === 0 ? (
            <div className="text-center py-6">
              <FileText className="w-8 h-8 text-[#E4E4E7] mx-auto mb-2" />
              <p className="text-sm text-[#71717A]">Nessun diario per questa data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map(log => (
                <div key={log.log_id} className="p-3 bg-[#8B5CF6]/5 rounded-lg space-y-2 border border-[#8B5CF6]/10"
                  data-testid={`admin-log-${log.log_id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#8B5CF6]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#09090B]">{log.user_name} {log.user_surname}</p>
                    </div>
                  </div>

                  {log.description && (
                    <p className="text-sm text-[#09090B] whitespace-pre-wrap bg-white rounded p-2">
                      {log.description}
                    </p>
                  )}

                  {log.media && log.media.length > 0 && (
                    <div className="grid grid-cols-4 gap-1.5">
                      {log.media.map(item => (
                        <button key={item.media_id} onClick={() => viewMedia(item)}
                          className="aspect-square rounded-md overflow-hidden border border-[#E4E4E7] bg-[#F4F4F5] flex items-center justify-center hover:border-[#8B5CF6] transition-colors"
                          data-testid={`admin-media-${item.media_id}`}>
                          {item.type === 'video' ? (
                            <Play className="w-5 h-5 text-[#8B5CF6]" />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-[#8B5CF6]" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {!log.description && (!log.media || log.media.length === 0) && (
                    <p className="text-xs text-[#71717A] italic">Diario vuoto</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Media viewer modal */}
      {viewingMedia && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => { if (viewingMedia.url) URL.revokeObjectURL(viewingMedia.url); setViewingMedia(null); }}>
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-2">
              {viewingMedia.type === 'video' ? (
                <video src={viewingMedia.url} controls className="w-full rounded" data-testid="admin-media-viewer-video" />
              ) : (
                <img src={viewingMedia.url} alt={viewingMedia.original_filename} className="w-full rounded" data-testid="admin-media-viewer-image" />
              )}
              <p className="text-xs text-[#71717A] mt-1 text-center">{viewingMedia.original_filename}</p>
              <Button variant="outline" className="w-full mt-2"
                onClick={() => { if (viewingMedia.url) URL.revokeObjectURL(viewingMedia.url); setViewingMedia(null); }}>
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminWorkLogs;
